<script>
   /****************************************************************
   * SelectivityPlot                                               *
   * --------------------                                          *
   * shows plot with number of alien samples for different alpha   *
   *****************************************************************/

   import { pnorm, qchisq } from 'mdatools/distributions';
   import { Vector } from 'mdatools/arrays';
   import { Axes, XAxis, YAxis, Box, Lines, Segments, Points } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, LINE_COLORS } from '../../../shared/plots/Defaults.js';

   export let params = {};                          // JSON with main plot parameters (not used)
   export let res;                                    // JSON with SIMCA results
   export let showGrid = true;                        // logical, show grid or not
   export let ncomp = 1;                              // number of components selected by user

   // plot saving parameters
   export let fileName = 'plot-selectivity-integral';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // number of points in the ROC curve
   const nPoints = 141; // 50 in [0, 0.1) and [0.90, 1.0) and 51 in [0.1, 0.9)

   // local copies of model parameters to check if update is necessary
   let curAlpha = 0, curBeta = 0, curNComp = ncomp, curLimType, curRes, getBeta;

   // method for computing pairs (1 - alpha, beta) and integral selectivity
   export function getPlotSeries(getBeta) {

      const x = Vector.zeros(nPoints);
      const y = Vector.zeros(nPoints);

      x.v[0] = 1;
      y.v[0] = 1;

      let i = 1;
      let sel = 0;

      // alpha from 0 to 0.1 (50 points)
      for (let a = 0.001; a < 0.1; a = a + 0.002) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.002
         i = i + 1;
      }

      // alpha from 0 to 0.1 (50 points)
      for (let a = 0.1; a < 0.9; a = a + 0.02) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.02
         i = i + 1;
      }

      // alpha from 0.9 to 1.0 (50 points)
      for (let a = 0.9; a < 1.0; a = a + 0.002) {
         const b = getBeta(a);
         x.v[i] = b;
         y.v[i] = 1 - a;
         sel = sel + b * 0.002
         i = i + 1;
      }

      x.v[i] = 0;
      y.v[i] = 0;

      return [x, y, 1 - sel];
   }


   // select first result object
   $: resobj = res ? res[Object.keys(res)[0]] : null;

   // trigger changes in current alpha
   $: if (resobj && curAlpha !== resobj.alpha) {
      curAlpha = resobj.alpha;
   }

   // change current result object only if number of components or limit type
   // has been changed
   $: if (resobj && (ncomp !== curNComp || resobj.limType !== curLimType)) {
      curRes = resobj.v[ncomp - 1];
      curLimType = resobj.limType;
      curNComp = ncomp;
   }

   // update getBeta lambda only if current result outcomes have changed
   $: if (curRes) {

      const f0 = curRes.f0;
      const k = curRes.k;
      const s = curRes.s;
      const h = curRes.hz;
      const Sz = curRes.Sz;
      const Mz = curRes.Mz;

      const norm1 = 1 / (k + s);
      const norm2 = 1 / Sz;

      getBeta = (a) => {
         const fcrit = qchisq(1 - a, k);
         const z = fcrit / f0;
         return pnorm((Math.pow(z * norm1, h) - Mz) * norm2);
      }
   }

   // if current Alpha or getBeta has changed compute new current Beta
   $: if (curAlpha && getBeta) {
      curBeta = getBeta(curAlpha / 100);
   }

   // compute new ROC coordinates if getBeta has been changed
   $: [x, y, sel] = getBeta ? getPlotSeries(getBeta) : [null, null, 0];
</script>

{#if x && y }
<Axes
   title={'Selectivity plot (A = ' + ncomp + ', AUC = ' + sel.toFixed(4) + ')'}
   xLabel="1 – selectivity, β"
   yLabel="Expected sensitivity, 1 – α"
   limX={[-0.05, 1.05]}
   limY={[-0.05, 1.05]}
   downloadLinks="hover"
   margins={MARGINS}
   {fileName} {pngWidth} {pngHeight} {pngRes}
>
   <!-- 50% line -->
   <Segments xStart={[0]} xEnd={[1]} yStart={[0]} yEnd={[1]} lineColor='#a0a0a0' lineType={3} />
   <!-- line with current alpha -->
   <Segments xStart={[-1]} xEnd={[1.1]} yStart={[1 - curAlpha/100]} yEnd={[1 - curAlpha/100]} lineColor='#a0a0a0' />

   <!-- data points -->
   <Lines xValues={x} yValues={y} lineWidth={1.5} lineColor={LINE_COLORS['alt']} />


   <!-- current model -->
   <Points
      xValues={[curBeta]}
      yValues={[1 - curAlpha/100]}
      markerSize={1}
      borderWidth={1}
      faceColor={LINE_COLORS['alt']}
      borderColor='#a0a0a0'
   />

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}